/**
 * Set (or edit) a savings goal. Built to feel like picking a prize, not filling in a
 * form: tap an emoji, name it, choose an amount. A kid edits only their own goal;
 * the Worker enforces that regardless of what this form sends.
 *
 * A failed save NEVER loses what was typed — the modal stays open with the values and
 * an inline error, so nothing has to be re-entered.
 */
import { useState, type FormEvent } from 'react';
import { api, ApiRequestError, money, parseMoney } from '../../lib/api';
import type { KidGoal, KidGoalInput, MoneyBucket } from '../../shared/types';
import {
  BUCKETS,
  Modal,
  bucketMeta,
  btnPrimary,
  btnSecondary,
  cx,
  fieldInput,
  fieldLabel,
} from './shared';

const EMOJI_CHOICES = ['🚲', '🎮', '🎧', '⚽', '📚', '🧸', '🛹', '🎨', '👟', '🐶', '🎁', '✨'];
const AMOUNT_PRESETS = [1000, 2500, 5000, 10000]; // cents: $10 $25 $50 $100

export default function GoalForm({
  goal,
  memberId,
  onClose,
  onSaved,
}: {
  /** Present = edit; absent = create. */
  goal?: KidGoal;
  /** Whose goal this is. For an adult creating a goal, the chosen kid; a kid: themselves. */
  memberId: number | null;
  onClose: () => void;
  onSaved: () => Promise<void>;
}) {
  const [emoji, setEmoji] = useState<string>(goal?.emoji ?? '🎯');
  const [name, setName] = useState(goal?.name ?? '');
  const [amount, setAmount] = useState(goal ? (goal.target / 100).toFixed(2) : '');
  const [bucket, setBucket] = useState<MoneyBucket>(goal?.bucket ?? 'save');
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setError(null);

    const cleanName = name.trim();
    if (!cleanName) {
      setError('Give your goal a name, like “New bike”.');
      return;
    }
    const cents = parseMoney(amount);
    if (cents === null || cents <= 0) {
      setError('How much does it cost? Try something like 40.');
      return;
    }

    const payload: KidGoalInput & { member_id?: number } = {
      name: cleanName,
      emoji,
      target: cents,
      bucket,
      target_date: null,
    };
    // An adult creating a goal must say which kid; a kid's own id is derived server-side.
    if (!goal && memberId != null) payload.member_id = memberId;

    setSaving(true);
    try {
      if (goal) await api.put(`/kidmoney/goals/${goal.id}`, payload);
      else await api.post('/kidmoney/goals', payload);
      await onSaved();
      onClose();
    } catch (err) {
      // Keep every field on screen — nothing typed is lost.
      setError(
        err instanceof ApiRequestError ? err.message : 'That did not save. Try again in a second.'
      );
      setSaving(false);
    }
  }

  const previewCents = parseMoney(amount);

  return (
    <Modal title={goal ? 'Edit your goal' : 'New savings goal'} onClose={onClose}>
      <form onSubmit={submit} className="space-y-5">
        {/* Big playful preview */}
        <div className="flex flex-col items-center gap-2 rounded-card bg-surface-sunk p-4 text-center">
          <span aria-hidden="true" className="text-5xl leading-none">
            {emoji}
          </span>
          <p className="font-display text-lg font-semibold text-ink">
            {name.trim() || 'Your goal'}
          </p>
          <p className="text-sm text-ink-soft">
            {previewCents && previewCents > 0 ? money(previewCents) : 'Pick an amount'}
          </p>
        </div>

        {/* Emoji picker */}
        <fieldset>
          <legend className={fieldLabel}>Pick a picture</legend>
          <div className="mt-2 grid grid-cols-6 gap-2">
            {EMOJI_CHOICES.map((e) => (
              <button
                key={e}
                type="button"
                onClick={() => setEmoji(e)}
                aria-pressed={emoji === e}
                aria-label={`Choose ${e}`}
                className={cx(
                  'grid h-11 w-full place-items-center rounded-xl border text-2xl transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sage',
                  emoji === e
                    ? 'border-sage bg-sage-soft'
                    : 'border-border bg-surface hover:bg-surface-sunk'
                )}
              >
                <span aria-hidden="true">{e}</span>
              </button>
            ))}
          </div>
        </fieldset>

        {/* Name */}
        <div>
          <label htmlFor="goal-name" className={fieldLabel}>
            What are you saving for?
          </label>
          <input
            id="goal-name"
            value={name}
            onChange={(ev) => setName(ev.target.value)}
            className={fieldInput}
            placeholder="New bike"
            maxLength={60}
            autoFocus={!goal}
            required
          />
        </div>

        {/* Amount */}
        <div>
          <label htmlFor="goal-amount" className={fieldLabel}>
            How much does it cost?
          </label>
          <div className="relative">
            <span
              aria-hidden="true"
              className="pointer-events-none absolute top-1/2 left-3 -translate-y-1/2 text-ink-faint"
            >
              $
            </span>
            <input
              id="goal-amount"
              value={amount}
              onChange={(ev) => setAmount(ev.target.value)}
              className={cx(fieldInput, 'pl-7')}
              inputMode="decimal"
              placeholder="40"
              required
            />
          </div>
          <div className="mt-2 flex flex-wrap gap-2">
            {AMOUNT_PRESETS.map((p) => (
              <button
                key={p}
                type="button"
                onClick={() => setAmount((p / 100).toFixed(2))}
                className="rounded-full border border-border bg-surface px-3 py-1.5 text-sm font-medium text-ink-soft hover:bg-surface-sunk focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sage"
              >
                {money(p)}
              </button>
            ))}
          </div>
        </div>

        {/* Bucket */}
        <fieldset>
          <legend className={fieldLabel}>Which bucket fills it up?</legend>
          <div className="mt-2 grid grid-cols-3 gap-2">
            {BUCKETS.map((b) => (
              <button
                key={b.key}
                type="button"
                onClick={() => setBucket(b.key)}
                aria-pressed={bucket === b.key}
                className={cx(
                  'flex flex-col items-center gap-1 rounded-xl border px-2 py-3 text-sm font-medium transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sage',
                  bucket === b.key
                    ? 'border-transparent ' + b.soft + ' ' + b.text
                    : 'border-border bg-surface text-ink-soft hover:bg-surface-sunk'
                )}
              >
                <span aria-hidden="true" className="text-xl">
                  {b.emoji}
                </span>
                {b.label}
              </button>
            ))}
          </div>
          <p className="mt-1.5 text-xs text-ink-faint">{bucketMeta(bucket).blurb}.</p>
        </fieldset>

        {error ? (
          <p role="alert" className="rounded-xl bg-clay-soft px-3 py-2 text-sm text-clay">
            {error}
          </p>
        ) : null}

        <div className="flex gap-2 pt-1">
          <button type="submit" disabled={saving} className={cx(btnPrimary, 'flex-1')}>
            {saving ? 'Saving…' : goal ? 'Save changes' : 'Start saving!'}
          </button>
          <button type="button" onClick={onClose} className={btnSecondary}>
            Cancel
          </button>
        </div>
      </form>
    </Modal>
  );
}
